// @ts-strict-ignore
import { FloatRangeVO } from '@project/components/article/model/vo/property.vo';
import { NumberUtils } from './number-utils';
import { TextSizeCheckRequestVO, TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { CellLabelSizeInfoUtils } from './cell-label-size-info-utils';
import { InitManager } from '../manager/init.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { TextureAreaVO } from '../vo/meta-element.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';

interface BestMatchingTextSizeCheckOptions {
  /** name of the font */
  fontName?: string | null;
  /** 0 or 1 */
  fontBold?: number;
  /** rotation yes or no (0 or 1) */
  rotation?: number;
  /** (max) amount of chars per line */
  chars?: number;
  /** amount of lines */
  lines?: number;
  /** amount of lines the text is multiplied over (defaults to 1) */
  multiLineCount?: number;
}

export class TextSizeCheckUtils {
  public static readonly DEFAULT_COMPRESSION: number = 100;

  public static getTextCheckIDFromParameters(c: string, s: number, sr: FloatRangeVO = null): string {
    const request: TextSizeCheckRequestVO = new TextSizeCheckRequestVO();
    request.textSizeCheckClass = c;
    request.spacing = s;
    request.spacingInRange = sr;
    return this.getTextCheckIDFromRequest(request);
  }

  public static getTextCheckIDFromRequest(request: TextSizeCheckRequestVO): string {
    if (!request) {
      return null;
    }
    if (NumberUtils.isNumber(request.spacing) || request.spacingInRange) {
      /* if (!request.spacingInRange) {
        request.spacingInRange = null;
      } */
      return JSON.stringify(request);
    } else {
      return request.textSizeCheckClass;
    }
  }

  public static getLinesWithText(text: string, includeEmptyLines: boolean = false): string[] {
    const lines: string[] = text ? text.split(new RegExp('/\n|\r|&#x0A;/g')) : [];
    const linesWithText: string[] = [];
    lines.forEach((textInLine: string) => {
      if (textInLine.length > 0 || includeEmptyLines) {
        linesWithText.push(textInLine);
      }
    });
    return linesWithText;
  }

  public static getMaxSizeTextInLines(lines: string[]): number {
    let maxTextSize: number = 0;
    for (const text of lines) {
      if (maxTextSize < text.length) {
        maxTextSize = text.length;
      }
    }
    return maxTextSize;
  }

  public static getLeastCompression(textSizeCheckInfos: TextSizeCheckVO[],
                                    textSizeCheckClass: string,
                                    fontBold: number,
                                    chars: number,
                                    lines: number,
                                    rotation: number,
                                    size: number,
                                    multiLineCount: number): number {
    const textSizeChecks: TextSizeCheckVO[] = CellLabelSizeInfoUtils.getMultiLineAdjustedCellLabelSizeInfos(
      textSizeCheckInfos,
      multiLineCount,
      rotation
    );
    let compression: number = NaN;
    if (textSizeChecks) {
      for (const textSizeCheck of textSizeChecks) {
        if (
          textSizeCheck &&
          (textSizeCheckClass === textSizeCheck.textSizeCheckClass || textSizeCheckClass === '') &&
          (fontBold === textSizeCheck.fontBold || fontBold === -1) &&
          (chars === textSizeCheck.chars || chars === -1) &&
          (lines === textSizeCheck.lines || lines === -1) &&
          (rotation === textSizeCheck.rotation || rotation === -1) &&
          (NumberUtils.isNotNumber(size) || textSizeCheck.fontSize === size)
        ) {
          if (NumberUtils.isNotNumber(compression) || TextSizeCheckUtils.DEFAULT_COMPRESSION > compression) {
            compression = NumberUtils.isNotNumber(compression) ? textSizeCheck.compression : Math.max(compression, textSizeCheck.compression);
          } else {
            break;
          }
        }
      }
    }
    return Math.min(compression, TextSizeCheckUtils.DEFAULT_COMPRESSION);
  }

  public static getMaxFontSize(textSizeCheckInfos: TextSizeCheckVO[],
                               textSizeCheckClass: string,
                               fontBold: number,
                               chars: number,
                               lines: number,
                               rotation: number,
                               compression: number,
                               multiLineCount: number): number {
    const textSizeCheckInfosToCheck: TextSizeCheckVO[] =
      CellLabelSizeInfoUtils.getMultiLineAdjustedCellLabelSizeInfos(textSizeCheckInfos, multiLineCount, rotation);
    let fontSize: number = NaN;
    textSizeCheckInfosToCheck.forEach((tsi: TextSizeCheckVO) => {
      if
      (
        tsi &&
        (textSizeCheckClass === tsi.textSizeCheckClass || textSizeCheckClass === '') &&
        (compression === tsi.compression) &&
        (fontBold === tsi.fontBold || fontBold === -1) &&
        (chars === tsi.chars || chars === -1) &&
        (lines === tsi.lines || lines === -1) &&
        (rotation === tsi.rotation || rotation === -1)) {
        if (NumberUtils.isNotNumber(fontSize) || tsi.fontSize > fontSize) {
          fontSize = tsi.fontSize;
        }
      }
    });
    return fontSize;
  }

  public static getCompressionByParameters(
    textSizeCheckInfos: TextSizeCheckVO[], textSizeCheckClass: string, fontBold: number, chars: number, lines: number, rotation: number,
    multiLineCount: number, size: number, useScaleFactor: boolean): number {
    let compression: number = TextSizeCheckUtils.getLeastCompression(
      textSizeCheckInfos, textSizeCheckClass, fontBold, chars, lines, rotation, size, multiLineCount);
    if (isNaN(compression)) {
      compression = TextSizeCheckUtils.DEFAULT_COMPRESSION;
    }
    if (useScaleFactor) {
      compression = Math.round(compression * InitManager.labelingScaleFactor);
    }
    if (compression > TextSizeCheckUtils.DEFAULT_COMPRESSION) {
      compression = TextSizeCheckUtils.DEFAULT_COMPRESSION;
    }
    return compression;
  }

  public static getTextCheckClassesByLabelingArticle(article: ArticleVO): string[] {
    if (!article) {
      return [];
    }

    const result: string[] = [];
    for (const metaElement of article.metaElements) {
      if (metaElement instanceof TextureAreaVO) {
        const textCheckClass: string = PropertyUtils.getStringPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS);
        if (textCheckClass) {
          result.push(textCheckClass);
        }
      }
    }
    return result;
  }

  /**
   * Selects the best matching text size check (the one with the highest compression, first one wins on ties).
   * Every option which is not set (undefined, null or NaN) is ignored.
   */
  public static getBestMatchingTextSizeCheck(
    textSizeCheckInfos: TextSizeCheckVO[],
    options: BestMatchingTextSizeCheckOptions = {},
  ): TextSizeCheckVO | undefined {
    const { fontName, multiLineCount = 1 } = options;
    const fontBold: number = options.fontBold ?? Number.NaN;
    const rotation: number = options.rotation ?? Number.NaN;
    const chars: number = options.chars ?? Number.NaN;
    const lines: number = options.lines ?? Number.NaN;
    const textSizeChecks: TextSizeCheckVO[] = CellLabelSizeInfoUtils.getMultiLineAdjustedCellLabelSizeInfos(
      textSizeCheckInfos,
      multiLineCount,
      rotation,
    );
    let bestMatch: TextSizeCheckVO | undefined;
    if (textSizeChecks) {
      for (const textSizeCheck of textSizeChecks) {
        if (
          textSizeCheck &&
          (!fontName || fontName === textSizeCheck.fontName) &&
          (NumberUtils.isNotNumber(fontBold) || fontBold === textSizeCheck.fontBold) &&
          (NumberUtils.isNotNumber(rotation) || rotation === textSizeCheck.rotation) &&
          (NumberUtils.isNotNumber(chars) || textSizeCheck.chars === chars) &&
          (NumberUtils.isNotNumber(lines) || textSizeCheck.lines === lines)
        ) {
          if (!bestMatch || bestMatch.compression < textSizeCheck.compression) {
            bestMatch = textSizeCheck;
          }
        }
      }
    }
    return bestMatch;
  }
}
